import { Injectable } from '@angular/core';
import { BehaviorSubject } from "rxjs";

export interface ModalAnimationOptions {
  animateIn?: string;  // animate.css class for opening animation
  animateOut?: string; // animate.css class for closing animation
  duration?: number;   // animation duration in milliseconds
}

@Injectable({
  providedIn: 'root'
})
export class BoostrapModalService {
  private modalState = new BehaviorSubject<string | null>(null);
  modalState$ = this.modalState.asObservable();

  modalShown: boolean = false;

  // Default animation options
  private defaultAnimations: ModalAnimationOptions = {
    animateIn: 'animate__fadeIn',
    animateOut: 'animate__fadeOut',
    duration: 300
  };

  /**
   * Opens a modal with optional animation effects
   * @param modalId The ID of the modal to open
   * @param animationOptions Optional animation settings
   */
  open(modalId: string, animationOptions?: ModalAnimationOptions) {
    // Merge provided options with defaults
    const options = { ...this.defaultAnimations, ...animationOptions };

    this.modalState.next(modalId);
    this.modalShown = true;

    setTimeout(() => {
      const modal = document.getElementById(modalId);
      if (modal) {
        const modalWrapper = document.getElementById('modal-wrapper');
        if (modalWrapper) {
          modalWrapper.classList.add('show');
        }

        // Add animate.css base class and specific animation class
        if (options.animateIn) {
          modal.classList.add('animate__animated', options.animateIn);

          // Set animation duration if provided
          if (options.duration) {
            modal.style.setProperty('--animate-duration', `${options.duration}ms`);
          }
        }

        modal.classList.add('show');
        modal.style.display = 'block';
        document.body.classList.add('modal-open');
      }
    }, 0);
  }

  /**
   * Closes the currently open modal with optional animation effects
   * @param animationOptions Optional animation settings
   */
  close(animationOptions?: ModalAnimationOptions) {
    this.modalShown = false;
    const modalId = this.modalState.getValue();

    if (modalId) {
      const modal = document.getElementById(modalId);
      const modalWrapper = document.getElementById('modal-wrapper');

      if (modalWrapper) {
        modalWrapper.classList.remove('show');
        modalWrapper.style.removeProperty('display');
      }

      if (modal) {
        // Merge provided options with defaults
        const options = { ...this.defaultAnimations, ...animationOptions };

        // If we have an out animation, play it before closing
        if (options.animateOut) {
          // Remove any existing animation classes
          modal.classList.remove('animate__animated');
          if (options.animateIn) {
            modal.classList.remove(options.animateIn);
          }

          // Add animate.css base class and specific out animation
          modal.classList.add('animate__animated', options.animateOut);

          // Set animation duration if provided
          if (options.duration) {
            modal.style.setProperty('--animate-duration', `${options.duration}ms`);
          }

          // Wait for animation to complete before hiding
          setTimeout(() => {
            this.hideModal(modal);
          }, options.duration || 300);
        } else {
          // No animation, hide immediately
          this.hideModal(modal);
        }
      }
    }
    this.modalState.next(null);
  }

  /**
   * Helper method to hide the modal and clean up
   */
  private hideModal(modal: HTMLElement) {
    modal.classList.remove('show', 'animate__animated');
    // Remove any animation classes
    modal.classList.remove(
      'animate__fadeIn', 'animate__fadeOut', 
      'animate__bounceIn', 'animate__bounceOut',
      'animate__slideInDown', 'animate__slideOutUp',
      'animate__zoomIn', 'animate__zoomOut'
    );
    modal.style.display = 'none';
    document.body.classList.remove('modal-open');
  }
}
